import { useMemo, useState } from 'react'
import { AlertTriangle, EyeOff, GitMerge, Pencil, Plus, RefreshCw } from 'lucide-react'
import {
  EmptyRow, ErpTable, KpiStrip, MetricCard, Panel, SectionHeader, Td, Th,
} from '@/components/erp/primitives'
import { Field, Modal } from '@/shared/ui/Modal'
import { FilterBar, SearchInput } from '@/shared/ui/filters'
import { ErrorStrip, Forbidden, LoadingBlock, Note } from '@/shared/ui/states'
import { useCan } from '@/shared/auth/session'
import { explain } from '@/shared/api/problem'
import { fmtNumber } from '@/shared/lib/format'
import { cn } from '@/lib/utils'
import type { TagMergeResult, TagVocabularyResponse } from '@/shared/api/types'
import { createTag, mergeTag, updateTag } from './api'
import { TagChip, canonicalPreview, canonicalProblem, useTagVocabulary } from './tags'

/** A card value that is honest about not knowing yet — never a zero for a null. */
const num = (n: number | null | false | undefined) =>
  n === null || n === undefined || n === false ? '—' : fmtNumber(n)

/**
 * The tag vocabulary.
 *
 * ## Why this screen exists at all
 *
 * `voter_tag_vocabulary` has existed since Phase 3 with no controller and no UI.
 * Tags were therefore born only by being applied to somebody, spelled however
 * the first person to type one spelled it — while the six occupation-based
 * targeting presets ask for exact strings. This screen is where those strings
 * get agreed on, before the fieldwork rather than after it.
 *
 * ## The three operations, and why they are three
 *
 * **Create** makes a tag before anybody carries it. That is the ordinary case:
 * a campaign manager sets up `pwd` and `student` in week one so the presets are
 * ready, and the fieldwork fills them in week three.
 *
 * **Edit** changes labels — display name, category, description, visibility. It
 * cannot change the canonical form, and the server refuses a request that tries.
 *
 * **Merge** is the only rename. Renaming in place would mean rewriting
 * `voter_tag`, every preset's `requires_tag` and every saved segment definition
 * in one go; merge moves the rows and then TELLS you what still points at the
 * old name, rather than quietly rewriting somebody's targeting rule as a side
 * effect of an admin action.
 */
export function TagManagerPage() {
  const canRead = useCan('voter:read')
  const canManage = useCan('voter:tag')

  const vocab = useTagVocabulary()
  const [q, setQ] = useState('')
  const [showHidden, setShowHidden] = useState(false)
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<TagVocabularyResponse | null>(null)
  const [merging, setMerging] = useState<TagVocabularyResponse | null>(null)

  // `?? []` here would render the whole KPI strip as "Tags 0 · Carried by nobody 0"
  // while the vocabulary is still loading, and permanently after a failed load —
  // the exact null-as-zero the table twenty lines below gets right.
  const loaded = vocab.tags
  const all = loaded ?? []
  const rows = useMemo(() => {
    const term = q.trim().toLowerCase()
    return all
      .filter((t) => showHidden || t.active)
      .filter((t) => {
        if (!term) return true
        const canonical = canonicalPreview(term)
        // `"boda".includes("")` is true, so a term like "--" that canonicalises to
        // nothing matched every row — while the field was calling it invalid.
        return (canonical !== '' && t.tag.includes(canonical))
          || t.displayName.toLowerCase().includes(term)
          || (t.category ?? '').toLowerCase().includes(term)
      })
      .sort((a, b) => b.voterCount - a.voterCount || a.tag.localeCompare(b.tag))
  }, [all, q, showHidden])

  if (!canRead) return <Forbidden what="the voter register" />

  const unused = all.filter((t) => t.voterCount === 0)
  const hidden = all.filter((t) => !t.active)

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Voter Tags"
        description="The controlled vocabulary the occupation-based targeting presets match on."
        right={(
          <>
            <button type="button" className="erp-btn" onClick={vocab.reload} aria-label="Refresh">
              <RefreshCw className="h-3.5 w-3.5" />
            </button>
            {canManage && (
              <button type="button" className="erp-btn erp-btn-primary" onClick={() => setCreating(true)}>
                <Plus className="h-3.5 w-3.5" /> New tag
              </button>
            )}
          </>
        )}
      />

      <ErrorStrip message={vocab.error} />

      <KpiStrip>
        <MetricCard label="Tags" value={num(loaded && all.length)} accent
          sub={loaded
            ? (hidden.length ? `${fmtNumber(hidden.length)} hidden from the picker` : 'All offered in the picker')
            : undefined} />
        <MetricCard
          label="Carried by nobody"
          value={num(loaded && unused.length)}
          sub={loaded ? (
            <span title="A targeting preset built on one of these matches nobody, and says so in the programme rule builder.">
              a preset on one of these matches nobody
            </span>
          ) : undefined}
        />
        <MetricCard label="Tagged voters"
          value={num(loaded && all.reduce((n, t) => n + t.voterCount, 0))}
          sub={loaded ? 'Counting a voter once per tag they carry' : undefined} />
        <MetricCard label="Categories"
          value={num(loaded && new Set(all.map((t) => t.category).filter(Boolean)).size)} />
      </KpiStrip>

      {vocab.truncated && (
        <Note tone="bad">
          The vocabulary is longer than this screen asks for and has been cut short. That was
          not supposed to be possible — a campaign's vocabulary is meant to be tens of
          entries. It is worth asking how it grew.
        </Note>
      )}

      <FilterBar>
        <SearchInput value={q} onChange={setQ} placeholder="Search tags" />
        <label className="flex items-center gap-1.5 text-erp-xs text-muted-foreground px-2">
          <input type="checkbox" checked={showHidden} onChange={(e) => setShowHidden(e.target.checked)} />
          Show hidden
        </label>
      </FilterBar>

      <Panel bodyClassName="p-0">
        {loaded === null ? (
          <div className="p-4">
            {vocab.error
              ? <p className="text-erp-sm text-muted-foreground">The vocabulary could not be loaded.</p>
              : <LoadingBlock label="Loading the vocabulary…" />}
          </div>
        ) : (
          <ErpTable head={(
            <>
              <Th>Tag</Th>
              <Th>Stored as</Th>
              <Th className="axis-hide-md">Category</Th>
              <Th className="text-right">Voters</Th>
              <Th className="axis-hide-md">Description</Th>
              <Th className="text-right">&nbsp;</Th>
            </>
          )}>
            {rows.length === 0 ? (
              <EmptyRow colSpan={6} message={all.length === 0
                ? 'No tags yet. Create the ones your targeting presets need — boda, mama_mboga, farmer, pwd, student — before the fieldwork starts.'
                : 'No tag matches that.'} />
            ) : rows.map((t) => (
              <tr key={t.tag} className={cn(!t.active && 'opacity-60')}>
                <Td>
                  <TagChip tag={t.tag} displayName={t.displayName} muted={!t.active} />
                  {!t.active && (
                    <span className="ml-1.5 inline-flex items-center gap-1 text-erp-xs text-muted-foreground">
                      <EyeOff className="h-3 w-3" /> hidden
                    </span>
                  )}
                </Td>
                {/* The canonical form gets its own column, not a tooltip. It is the
                    string a targeting rule compares against, and the gap between it
                    and the label is where the six presets went wrong. */}
                <Td className="font-mono text-erp-xs">{t.tag}</Td>
                <Td className="axis-hide-md text-erp-xs text-muted-foreground">{t.category ?? '—'}</Td>
                <Td className="text-right tabular-nums">
                  {t.voterCount === 0
                    ? <span className="text-amber-700 dark:text-amber-300" title="A preset built on this tag currently matches nobody.">0</span>
                    : fmtNumber(t.voterCount)}
                </Td>
                <Td className="axis-hide-md max-w-[18rem]">
                  <span className="truncate block text-erp-xs text-muted-foreground" title={t.description ?? undefined}>
                    {t.description ?? '—'}
                  </span>
                </Td>
                <Td className="text-right">
                  {canManage && (
                    <div className="inline-flex gap-1">
                      <button type="button" className="erp-btn h-7" onClick={() => setEditing(t)}
                        aria-label={`Edit ${t.displayName}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button type="button" className="erp-btn h-7" onClick={() => setMerging(t)}
                        aria-label={`Merge ${t.displayName}`} title="Merge into another tag — the only way to rename one">
                        <GitMerge className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                </Td>
              </tr>
            ))}
          </ErpTable>
        )}
      </Panel>

      <Note tone="info">
        Hiding a tag removes it from the pickers and changes <span className="font-medium">nothing
        else</span>: the voters keep it, the presets keep matching it, saved segments keep
        refreshing on it. It is "stop offering this", never "stop counting this" — there is
        no delete, because deleting a tag would silently change what a targeting rule returns.
      </Note>

      {creating && (
        <CreateDialog onClose={() => setCreating(false)}
          onSaved={() => { setCreating(false); vocab.reload() }} />
      )}
      {editing && (
        <EditDialog tag={editing} onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); vocab.reload() }}
          onMergeInstead={() => { setMerging(editing); setEditing(null) }} />
      )}
      {merging && (
        <MergeDialog tag={merging} vocabulary={all} onClose={() => setMerging(null)}
          onMerged={() => vocab.reload()} />
      )}
    </div>
  )
}

// ── create ──────────────────────────────────────────────────────────────────

function CreateDialog({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [displayName, setDisplayName] = useState('')
  const [tag, setTag] = useState('')
  const [category, setCategory] = useState('')
  const [description, setDescription] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /** Untouched, the canonical form follows the label — which is what people expect. */
  const [tagEdited, setTagEdited] = useState(false)

  const effective = tagEdited ? tag : displayName
  const canonical = canonicalPreview(effective)
  const problem = effective.trim() ? canonicalProblem(effective) : null

  const submit = async () => {
    setBusy(true); setError(null)
    try {
      await createTag({
        tag: effective,
        displayName: displayName.trim() || undefined,
        category: category.trim() || undefined,
        description: description.trim() || undefined,
      })
      onSaved()
    } catch (e) { setError(explain(e)) } finally { setBusy(false) }
  }

  return (
    <Modal open onClose={onClose} title="New tag" width="md"
      description="A tag can exist before anybody carries it — that is the point of creating one here."
      footer={(
        <>
          <button type="button" className="erp-btn" onClick={onClose}>Cancel</button>
          <button type="button" className="erp-btn-primary"
            disabled={busy || !canonical || Boolean(problem) || !displayName.trim()}
            onClick={() => void submit()}>{busy ? 'Creating…' : 'Create'}</button>
        </>
      )}>
      <div className="space-y-3">
        <ErrorStrip message={error} />
        <Field label="Name" required hint="What people read. “Boda boda riders”.">
          <input className="erp-input w-full" value={displayName} autoFocus maxLength={160}
            onChange={(e) => setDisplayName(e.target.value)} />
        </Field>
        <Field
          label="Stored as"
          required
          hint="What targeting rules match, exactly and case-sensitively. Edit it only if a preset demands a particular string."
        >
          {/* `maxLength` mirrors the server's `@Size(max = 64)`, which is on the RAW
              value — so a long string that collapses to something short is still
              refused, and refusing it here is cheaper than a 400. */}
          <input className="erp-input w-full font-mono" maxLength={64}
            value={tagEdited ? tag : canonical}
            onChange={(e) => { setTagEdited(true); setTag(e.target.value) }} />
        </Field>
        {problem
          ? <p className="text-erp-xs text-red-700 dark:text-red-300">{problem}</p>
          // Only when the box does not already show it. Untouched, the field IS the
          // canonical form and repeating it underneath is noise; once somebody types
          // into it directly, the line is the only place the transformation shows.
          : canonical && tagEdited && tag !== canonical && (
            <p className="text-erp-xs text-muted-foreground">
              Will be stored as <span className="font-mono">{canonical}</span>.
            </p>
          )}
        <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
          <Field label="Category" hint="Free text. Groups the picker.">
            <input className="erp-input w-full" value={category} maxLength={32}
              onChange={(e) => setCategory(e.target.value)} placeholder="occupation" />
          </Field>
          <Field label="Description" hint="Who qualifies for this tag.">
            <input className="erp-input w-full" value={description} maxLength={500}
              onChange={(e) => setDescription(e.target.value)} />
          </Field>
        </div>
      </div>
    </Modal>
  )
}

// ── edit ────────────────────────────────────────────────────────────────────

function EditDialog({ tag, onClose, onSaved, onMergeInstead }: {
  tag: TagVocabularyResponse
  onClose: () => void
  onSaved: () => void
  onMergeInstead: () => void
}) {
  const [displayName, setDisplayName] = useState(tag.displayName)
  const [category, setCategory] = useState(tag.category ?? '')
  const [description, setDescription] = useState(tag.description ?? '')
  const [active, setActive] = useState(tag.active)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async () => {
    setBusy(true); setError(null)
    try {
      // `tag` is never sent. The server binds it solely to refuse it, and a client
      // that sends it gets a 400 — so not sending it is the contract, not caution.
      // `null` on the wire means "leave alone" — so an empty box has to be sent as
      // `undefined`, not `""`. Sending `""` turned a tag with no category into one
      // with an empty-string category on the first unrelated edit, and the table
      // then renders a blank cell where it used to render an em dash.
      const blankToUndefined = (v: string) => (v.trim() ? v.trim() : undefined)
      await updateTag(tag.tag, {
        displayName: displayName.trim(),
        category: blankToUndefined(category),
        description: blankToUndefined(description),
        active,
      })
      onSaved()
    } catch (e) { setError(explain(e)) } finally { setBusy(false) }
  }

  return (
    <Modal open onClose={onClose} title={`Edit ${tag.displayName}`} width="md"
      footer={(
        <>
          <button type="button" className="erp-btn" onClick={onClose}>Cancel</button>
          <button type="button" className="erp-btn-primary" disabled={busy || !displayName.trim()}
            onClick={() => void submit()}>{busy ? 'Saving…' : 'Save'}</button>
        </>
      )}>
      <div className="space-y-3">
        <ErrorStrip message={error} />

        <div className="rounded-md border border-border bg-muted/30 p-3">
          <div className="text-erp-xs uppercase tracking-wide text-muted-foreground">Stored as</div>
          <div className="font-mono text-erp-sm">{tag.tag}</div>
          <p className="mt-1.5 text-erp-xs text-muted-foreground">
            This cannot be edited. It is referenced by targeting presets and saved segments, so
            changing it is a migration rather than a field edit —{' '}
            <button type="button" className="underline underline-offset-2 text-chocolate"
              onClick={onMergeInstead}>merge it into another tag</button>{' '}
            to rename it.
          </p>
        </div>

        <Field label="Name" required>
          <input className="erp-input w-full" value={displayName} autoFocus maxLength={160}
            onChange={(e) => setDisplayName(e.target.value)} />
        </Field>
        <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
          <Field label="Category">
            <input className="erp-input w-full" value={category} maxLength={32}
              onChange={(e) => setCategory(e.target.value)} />
          </Field>
          <Field label="Description">
            <input className="erp-input w-full" value={description} maxLength={500}
              onChange={(e) => setDescription(e.target.value)} />
          </Field>
        </div>

        <label className="flex items-start gap-2 text-erp-sm">
          <input type="checkbox" className="mt-0.5" checked={!active}
            onChange={(e) => setActive(!e.target.checked)} />
          <span>
            Hide from the pickers
            <span className="block text-erp-xs text-muted-foreground">
              {tag.voterCount > 0
                ? `The ${fmtNumber(tag.voterCount)} voters carrying it keep it, and every preset and segment that matches it goes on matching it. Hiding is not deleting.`
                : 'Nobody carries this tag, so hiding it only removes it from the pickers.'}
            </span>
          </span>
        </label>
      </div>
    </Modal>
  )
}

// ── merge ───────────────────────────────────────────────────────────────────

/**
 * Fold one tag into another.
 *
 * The result is the important part of this dialog, not the form. A merge moves
 * voter rows and rewrites no targeting rule — so a preset whose `requires_tag`
 * names the source tag now names a tag nobody carries, and the ONLY place that
 * fact surfaces is here. Rendering `affectedPresetKeys` and `affectedSegmentIds`
 * is the difference between an admin action and a silent outage.
 */
function MergeDialog({ tag, vocabulary, onClose, onMerged }: {
  tag: TagVocabularyResponse
  vocabulary: TagVocabularyResponse[]
  onClose: () => void
  onMerged: () => void
}) {
  const [into, setInto] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<TagMergeResult | null>(null)

  const canonical = canonicalPreview(into)
  // Every other entry point in this feature runs `canonicalProblem`; this one did
  // not, so "boda!" rendered "Target boda! — does not exist yet and will be
  // created" and then took a 400.
  const problem = into.trim() ? canonicalProblem(into) : null
  const target = vocabulary.find((v) => v.tag === canonical)
  const sameTag = canonical === tag.tag

  const submit = async () => {
    setBusy(true); setError(null)
    try {
      setResult(await mergeTag(tag.tag, canonical))
      onMerged()
    } catch (e) { setError(explain(e)) } finally { setBusy(false) }
  }

  return (
    <Modal open onClose={onClose} width="lg"
      title={result ? 'Merged' : `Merge ${tag.displayName}`}
      description={result ? undefined : 'Moves every voter carrying this tag onto another one. This is also the only way to rename a tag.'}
      footer={result ? (
        <button type="button" className="erp-btn-primary" onClick={onClose}>Close</button>
      ) : (
        <>
          <button type="button" className="erp-btn" onClick={onClose}>Cancel</button>
          <button type="button" className="erp-btn-primary"
            disabled={busy || !canonical || sameTag || Boolean(problem)}
            onClick={() => void submit()}>
            {busy ? 'Merging…' : 'Merge'}
          </button>
        </>
      )}>
      {result ? <MergeOutcome result={result} /> : (
        <div className="space-y-3">
          <ErrorStrip message={error} />
          <p className="text-erp-sm">
            <span className="font-mono">{tag.tag}</span>
            {' '}carries <span className="font-medium">{fmtNumber(tag.voterCount)}</span> voters.
          </p>

          <Field label="Merge into" required
            hint="An existing tag, or a new name — the target is created if it does not exist.">
            <input className="erp-input w-full font-mono" value={into} autoFocus maxLength={64}
              onChange={(e) => setInto(e.target.value)} placeholder="boda" />
          </Field>
          {problem && <p className="text-erp-xs text-red-700 dark:text-red-300">{problem}</p>}
          {canonical && !problem && (
            <p className={cn('text-erp-xs', sameTag ? 'text-red-700 dark:text-red-300' : 'text-muted-foreground')}>
              {sameTag
                ? 'A tag cannot be merged into itself.'
                : (
                  <>
                    Target <span className="font-mono">{canonical}</span>
                    {target
                      ? ` — exists, ${fmtNumber(target.voterCount)} voters. Anyone carrying both keeps one row.`
                      : ' — does not exist yet and will be created.'}
                  </>
                )}
            </p>
          )}

          <Note tone="bad">
            This does not rewrite any targeting rule. Presets and saved segments that name{' '}
            <span className="font-mono">{tag.tag}</span> will still name it afterwards, and will
            then match nobody. The next screen lists exactly which ones, so you can fix them.
          </Note>

          <p className="text-erp-xs text-muted-foreground">
            Who applied each tag, and when, is carried across unchanged. A merge corrects the
            label; it does not rewrite the fieldwork.
          </p>
        </div>
      )}
    </Modal>
  )
}

function MergeOutcome({ result }: { result: TagMergeResult }) {
  const orphaned = result.affectedPresetKeys.length + result.affectedSegmentIds.length
  return (
    <div className="space-y-3">
      <p className="text-erp-sm">
        <span className="font-mono">{result.from}</span> → <span className="font-mono">{result.into}</span>
      </p>
      <ul className="text-erp-sm space-y-1">
        <li><span className="font-medium tabular-nums">{fmtNumber(result.moved)}</span> voters moved</li>
        <li>
          <span className="font-medium tabular-nums">{fmtNumber(result.alreadyPresent)}</span>{' '}
          already carried both — one row kept, not two
        </li>
      </ul>

      {orphaned === 0 ? (
        <Note tone="good">
          No targeting preset or saved segment referred to that tag, so nothing else needs
          changing.
        </Note>
      ) : (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 dark:border-amber-900/50 dark:bg-amber-950/40">
          <div className="flex items-start gap-2 text-erp-sm text-amber-900 dark:text-amber-200">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" aria-hidden />
            <div className="min-w-0">
              <p className="font-medium">
                {orphaned} {orphaned === 1 ? 'rule still names' : 'rules still name'}{' '}
                <span className="font-mono">{result.from}</span>
              </p>
              {/* NOT "each of these now matches nobody". A preset requiring
                  ['casual','hawker','trader','boda','driver'] still matches on four
                  other tags after `boda` is merged away — it is degraded, not dead.
                  Stating the strong version as certainty sends somebody to fix five
                  rules, one of which is fine. */}
              <p className="text-erp-xs mt-0.5">
                Nobody carries that tag any more. A rule that named only it now matches
                nobody; one that named it alongside other tags has lost a term. Point each
                at <span className="font-mono">{result.into}</span>.
              </p>
              {result.affectedPresetKeys.length > 0 && (
                <p className="text-erp-xs mt-1.5">
                  <span className="font-medium">Targeting presets:</span>{' '}
                  {result.affectedPresetKeys.join(', ')}
                </p>
              )}
              {/* The ids are in scope. Telling the reader to go and find them in an
                  audit entry, while holding them, is a refusal pointing at nothing. */}
              {result.affectedSegmentIds.length > 0 && (
                <div className="text-erp-xs mt-1">
                  <span className="font-medium">Saved segments:</span>{' '}
                  <span className="font-mono break-all">
                    {result.affectedSegmentIds.join(', ')}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
